import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Container, Paper, Typography, Alert, Button, CircularProgress, Box, Stack, Chip } from '@mui/material';
import featuredService from '../../services/featuredService';
import { PLANS, FEATURES } from '../../components/Reszletek/PricingSection';

// Ingyenes korai ajánlat átvétele: kártya és számlázási adatok nélkül, kategóriánként egy kattintással.
const PACKAGES = {
  EARLY: '3 hónap Kiemelés, utána 6 hónap Előfizetés',
  LATE: '9 hónap Előfizetés',
};
// A csomagok tartalma a /reszletek árazás előnylistájából (ugyanaz a forrás, nem kell külön karbantartani)
const PHASES = {
  EARLY: [
    { plan: 'TIER2', duration: '3 hónap' },
    { plan: 'TIER3', duration: 'majd 6 hónap' },
  ],
  LATE: [{ plan: 'TIER3', duration: '9 hónap' }],
};

const planBenefits = (planKey) => FEATURES.flatMap((f) => {
  if (f.key === 'commission') return ['Nincs jutalék sikeres foglalás után'];
  const value = f.values[planKey];
  if (value === true) return [f.label];
  if (typeof value === 'string') return [`${f.label}: ${value}`];
  return [];
});

const PackageContents = ({ packageKey }) => (
  <Stack spacing={1.5} sx={{ my: 1.5 }}>
    {PHASES[packageKey].map(({ plan, duration }) => (
      <Box key={plan} sx={{ bgcolor: 'rgba(42,93,50,0.06)', borderRadius: 1, p: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {duration}: {PLANS.find(p => p.key === plan).name}
        </Typography>
        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
          {planBenefits(plan).map(text => (
            <Typography component="li" variant="body2" key={text}>{text}</Typography>
          ))}
        </Box>
      </Box>
    ))}
  </Stack>
);

const REASONS = {
  ALREADY_CLAIMED: 'Ezt az ajánlatot ebben a kategóriában már átvetted.',
  HAS_SUBSCRIPTION: 'Ebben a kategóriában már van aktív kiemelésed vagy előfizetésed.',
};

const EarlyOfferPage = () => {
  const [offer, setOffer] = useState(null);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [done, setDone] = useState(null); // { categoryName, package }

  const load = () => featuredService.getEarlyOffer()
    .then((response) => setOffer(response?.data || response))
    .catch((err) => setError(err?.message || 'Nem sikerült betölteni az ajánlatot.'));

  useEffect(() => { load(); }, []);

  const handleClaim = async (category) => {
    setBusyId(category.id);
    setError('');
    try {
      const response = await featuredService.claimEarlyOffer(category.id);
      setDone({ categoryName: category.name, package: (response?.data || response).package });
      await load();
    } catch (err) {
      setError(err?.message || 'Az átvétel nem sikerült.');
      await load();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>Köszönjük, hogy korán bíztál bennünk</Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Az ajánlat <strong>ingyenes, és kártyaadatok vagy számlázási adatok megadása nélkül</strong> átvehető.
          A lejárat után automatikusan az ingyenes csomagba kerülsz vissza, nincs mit lemondanod.
        </Typography>

        {!offer && !error && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 3 }} />}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {done && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Kész! A(z) {done.categoryName} kategóriában aktiváltuk: {PACKAGES[done.package]}.
          </Alert>
        )}

        {offer && !offer.open && (
          <Alert severity="info">Az ajánlat jelenleg nem elérhető, vagy a profilod még nincs jóváhagyva.</Alert>
        )}

        {offer?.open && (
          <Stack spacing={2}>
            {offer.categories.map((category) => (
              <Box key={category.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{category.name}</Typography>
                {category.canClaim ? (
                  <>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {PACKAGES[category.package]}
                      {category.package === 'EARLY' && (
                        <Chip size="small" color="success" sx={{ ml: 1 }} label={`Még ${category.earlySlotsLeft} korai hely`} />
                      )}
                    </Typography>
                    <PackageContents packageKey={category.package} />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                      Díjmentes, kártyaadat nélkül. Nem tartalmazza a Kiemelés prémium csomagot. Lejárat után automatikusan az ingyenes csomagba kerülsz vissza.
                    </Typography>
                    <Button variant="contained" disabled={busyId !== null} onClick={() => handleClaim(category)}>
                      {busyId === category.id ? <CircularProgress size={22} color="inherit" /> : 'Átveszem'}
                    </Button>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">{REASONS[category.reason] || 'Nem elérhető.'}</Typography>
                )}
              </Box>
            ))}
          </Stack>
        )}

        <Box sx={{ mt: 3 }}>
          <Button component={Link} to="/provider/featured" variant="text">Kiemelések és előfizetések</Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default EarlyOfferPage;
